﻿import type { InvoiceInput } from "../../../shared/src/index";

import { setAnalyticsContext, trackConversion } from "./analytics";

declare global {
  interface Window {
    __COMPLIANCE_API_BASE_URL__?: string;
    __COMPLIANCE_API_REGION_MAP__?: Record<string, string>;
  }
}

let activeOrganizationId = "";
let activeCountry = "SG";
let activeLocale = "en";

export class ApiError extends Error {
  code?: string;
  details?: unknown;
  status?: number;

  constructor(message: string, options: { code?: string; details?: unknown; status?: number } = {}) {
    super(message);
    this.name = "ApiError";
    this.code = options.code;
    this.details = options.details;
    this.status = options.status;
  }
}

const resolveBaseURL = (): string => {
  const mapped = window.__COMPLIANCE_API_REGION_MAP__?.[activeCountry];
  return mapped ?? window.__COMPLIANCE_API_BASE_URL__ ?? "/api";
};

const request = async <T>(path: string, init: RequestInit = {}): Promise<T> => {
  const response = await fetch(`${resolveBaseURL()}${path}`, {
    credentials: "include",
    ...init,
    headers: {
      "content-type": "application/json",
      "x-locale": activeLocale,
      ...(activeOrganizationId ? { "x-organization-id": activeOrganizationId } : {}),
      ...(init.headers ?? {})
    }
  });
  const payload = (await response.json()) as {
    success: boolean;
    data?: T;
    error?: { code?: string; message: string; details?: unknown };
  };
  if (!response.ok || !payload.success || !payload.data) {
    throw new ApiError(payload.error?.message ?? `Request failed: ${response.status}`, {
      code: payload.error?.code,
      details: payload.error?.details,
      status: response.status
    });
  }
  return payload.data;
};

export const api = {
  setOrganizationId(value: string) {
    activeOrganizationId = value;
    setAnalyticsContext({ organizationId: value });
  },
  setCountry(value: string) {
    activeCountry = value;
    setAnalyticsContext({ country: value });
  },
  setLocale(value: string) {
    activeLocale = value;
    setAnalyticsContext({ locale: value });
  },
  bootstrap() {
    return request<{
      countries: unknown[];
      locales: string[];
      persistenceMode: string;
      aiAvailable: boolean;
      emailVerificationEnabled: boolean;
      mailConfigured: boolean;
      captcha: {
        enabled: boolean;
        region: "cn" | "sgp";
        prefix: string;
        sceneId: string;
      };
      billing: {
        enabled: boolean;
        environment: "sandbox" | "production";
        clientToken: string;
      };
      multiRegionReady: boolean;
      launchCountries: string[];
      expansionReadyCountries: string[];
      deploymentRequirements: Array<{ key: string; required: boolean; category: string; description: string }>;
    }>("/meta/bootstrap");
  },
  session() {
    return request<{
      authenticated: boolean;
      user?: { id: string; email: string; displayName: string; locale: string; emailVerifiedAt?: string | null };
      organizations?: Array<{ id: string; name: string; country: string; role: string; planCode: string; billingStatus: string }>;
      defaultOrganizationId?: string | null;
      verificationRequired?: boolean;
      isBillingAdmin?: boolean;
    }>("/auth/session");
  },
  register(payload: { email: string; password: string; displayName: string; locale: string; organizationName: string; organizationCountry: string; billingEmail?: string; captchaVerifyParam?: string }) {
    return request<{
      authenticated: boolean;
      user: { id: string; email: string; displayName: string; locale: string; emailVerifiedAt?: string | null };
      organizations: Array<{ id: string; name: string; country: string; role: string; planCode: string; billingStatus: string }>;
      defaultOrganizationId?: string | null;
      verificationRequired?: boolean;
      isBillingAdmin?: boolean;
      verificationDelivery?: { delivered: boolean; reason?: string; cooldownSeconds?: number };
    }>("/auth/register", {
      method: "POST",
      body: JSON.stringify(payload)
    }).then((response) => {
      trackConversion("auth_register_success", { organizationCountry: payload.organizationCountry });
      return response;
    });
  },
  login(payload: { email: string; password: string; captchaVerifyParam?: string }) {
    return request<{
      authenticated: boolean;
      user: { id: string; email: string; displayName: string; locale: string; emailVerifiedAt?: string | null };
      organizations: Array<{ id: string; name: string; country: string; role: string; planCode: string; billingStatus: string }>;
      defaultOrganizationId?: string | null;
      verificationRequired?: boolean;
      isBillingAdmin?: boolean;
      verificationDelivery?: { delivered: boolean; reason?: string; cooldownSeconds?: number };
    }>("/auth/login", {
      method: "POST",
      body: JSON.stringify(payload)
    }).then((response) => {
      trackConversion("auth_login_success");
      return response;
    });
  },
  forgotPassword(payload: { email: string; captchaVerifyParam?: string }) {
    return request<{ accepted: boolean; cooldownSeconds?: number }>("/auth/forgot-password", {
      method: "POST",
      body: JSON.stringify(payload)
    });
  },
  resetPassword(payload: { email: string; code: string; password: string }) {
    return request<{ reset: boolean }>("/auth/reset-password", {
      method: "POST",
      body: JSON.stringify(payload)
    });
  },
  changePassword(payload: { currentPassword: string; password: string }) {
    return request<{ changed: boolean }>("/auth/change-password", {
      method: "POST",
      body: JSON.stringify(payload)
    });
  },
  verifyEmail(payload: { code: string }) {
    return request<{
      authenticated?: boolean;
      user?: { id: string; email: string; displayName: string; locale: string; emailVerifiedAt?: string | null };
      organizations?: Array<{ id: string; name: string; country: string; role: string; planCode: string; billingStatus: string }>;
      defaultOrganizationId?: string | null;
      verificationRequired?: boolean;
      isBillingAdmin?: boolean;
      verified?: boolean;
    }>("/auth/verify-email", {
      method: "POST",
      body: JSON.stringify(payload)
    });
  },
  resendVerification(payload: { captchaVerifyParam?: string } = {}) {
    return request<{ delivered: boolean; reason?: string; cooldownSeconds?: number }>("/auth/resend-verification", {
      method: "POST",
      body: JSON.stringify(payload)
    });
  },
  logout() {
    return request<{ ok: boolean }>("/auth/logout", {
      method: "POST",
      body: JSON.stringify({})
    });
  },
  check(invoice: InvoiceInput) {
    return request<{ result: unknown; saved: unknown; sources: unknown[] }>("/compliance/check", {
      method: "POST",
      body: JSON.stringify(invoice)
    }).then((response) => {
      trackConversion("compliance_check_success", { country: invoice.country });
      return response;
    });
  },
  generate(invoice: InvoiceInput) {
    return request<{ result: unknown; html: string; saved: unknown }>("/invoice/generate", {
      method: "POST",
      body: JSON.stringify(invoice)
    }).then((response) => {
      trackConversion("invoice_preview_generated", { country: invoice.country });
      return response;
    });
  },
  extract(payload: { country: string; locale: string; text?: string; fileBase64?: string; fileName?: string; imageBase64?: string; mimeType?: string }) {
    return request<{ available: boolean; draft?: unknown; raw?: string; reason?: string }>("/intake/extract", {
      method: "POST",
      body: JSON.stringify(payload)
    }).then((response) => {
      trackConversion("intake_extract_success", { country: payload.country, mimeType: payload.mimeType, available: response.available });
      return response;
    });
  },
  runs(limit = 20) {
    return request<unknown[]>(`/runs?limit=${limit}`);
  },
  invoice(invoiceId: string) {
    return request<unknown>(`/invoices/${invoiceId}`);
  },
  invoices(limit = 50) {
    return request<unknown[]>(`/invoices?limit=${limit}`);
  },
  billingOverview() {
    return request<{
      organization: {
        id: string;
        name: string;
        country: string;
        planCode: string;
        billingStatus: string;
        billingCycle: string;
        billingCurrentPeriodStart: string | null;
        billingCurrentPeriodEnd: string | null;
        billingNextBilledAt: string | null;
      };
      usage: { monthStart: string; checks: number; extractions: number; generatedInvoices: number; members: number };
      limits: { monthlyPriceUsd: number; checksPerMonth: number; extractionsPerMonth: number; generatedInvoicesPerMonth: number; members: number; historyDays: number };
      availablePlans: Array<{ code: string; monthlyPriceUsd: number; checksPerMonth: number; extractionsPerMonth: number; generatedInvoicesPerMonth: number; members: number; historyDays: number }>;
      subscription: {
        id: string;
        providerSubscriptionId: string;
        planCode: string;
        status: string;
        currency?: string;
        currentPeriodStart?: string;
        currentPeriodEnd?: string;
        nextBilledAt?: string;
        managementUrls?: Record<string, string | null>;
      } | null;
      orders: Array<{
        id: string;
        providerTransactionId: string;
        planCode: string;
        status: string;
        currency?: string;
        totalAmount?: number;
        refundedAmount: number;
        createdAt: string;
        completedAt?: string;
      }>;
      provider: {
        enabled: boolean;
        environment: "sandbox" | "production";
        clientToken: string;
      };
    }>("/billing/overview");
  },
  billingOrders(limit = 20) {
    return request<
      Array<{
        id: string;
        providerTransactionId: string;
        planCode: string;
        status: string;
        currency?: string;
        totalAmount?: number;
        refundedAmount: number;
        createdAt: string;
        completedAt?: string;
      }>
    >(`/billing/orders?limit=${limit}`);
  },
  createCheckout(payload: { planCode: string }) {
    return request<{ transactionId: string; checkoutUrl?: string }>("/billing/checkout", {
      method: "POST",
      body: JSON.stringify(payload)
    }).then((response) => {
      trackConversion("billing_checkout_started", { planCode: payload.planCode });
      return response;
    });
  },
  confirmCheckout(payload: { transactionId: string }) {
    return request<{ transactionId: string; planCode: string; overview: unknown }>("/billing/checkout/confirm", {
      method: "POST",
      body: JSON.stringify(payload)
    }).then((response) => {
      trackConversion("billing_checkout_confirmed", { planCode: response.planCode });
      return response;
    });
  },
  subscriptionManagement() {
    return request<{ managementUrls: Record<string, string | null> | null }>("/billing/subscription/manage", {
      method: "POST",
      body: JSON.stringify({})
    });
  },
  adminBillingOverview() {
    return request<{
      plans: Array<{ code: string; monthlyPriceUsd: number }>;
      counts: {
        orders: number;
        completedOrders: number;
        activeSubscriptions: number;
        refundPending: number;
        refundedOrders: number;
      };
      recentOrders: Array<{
        id: string;
        providerTransactionId: string;
        organizationId: string;
        organizationName: string;
        planCode: string;
        status: string;
        currency?: string;
        totalAmount?: number;
        refundedAmount: number;
        createdAt: string;
        completedAt?: string;
      }>;
    }>("/admin/billing/overview");
  },
  adminBillingOrders(limit = 50) {
    return request<
      Array<{
        id: string;
        providerTransactionId: string;
        organizationId: string;
        organizationName: string;
        planCode: string;
        status: string;
        currency?: string;
        totalAmount?: number;
        refundedAmount: number;
        createdAt: string;
        completedAt?: string;
      }>
    >(`/admin/billing/orders?limit=${limit}`);
  },
  adminRefundOrder(orderId: string, payload: { reason?: string }) {
    return request<{ refunded: boolean; orderId: string; adjustmentId: string; status: string }>(`/admin/billing/orders/${orderId}/refund`, {
      method: "POST",
      body: JSON.stringify(payload)
    });
  }
};
